import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AnimatePresence, motion } from 'motion/react';
import { DEFAULT_ANIMATION_DURATION, POLL_INTERVALL_STARTING } from '@/config';
import useApi from '@/hooks/useApi';
import SuccessBanner from '@/components/SuccessBanner';

// After this long in STARTING, swap to the reassuring "still preparing" copy
// (document preparation shells out to Ghostscript and can take a while for
// large files).
const SLOW_PREPARE_THRESHOLD = 1_000 * 60;

type SendPhase = 'preparing' | 'review' | 'sent';

interface SendProgressBannerProps {
  workflowId: string;
  name: string;
  hasReviewers: boolean;
  onDismiss: () => void;
}

// Live banner for a just-sent signature collection. The backend keeps the
// signing in STARTING while it prepares the documents (signature page, PDF/A
// conversion, upload) and only notifies signers once it flips to SIGNING —
// so the banner starts in a "preparing" tone and morphs to the success
// confirmation when the poll observes that transition. With reviewers the
// signing goes straight to IN_REVIEW, so no polling is needed.
const SendProgressBanner = ({
  workflowId,
  name,
  hasReviewers,
  onDismiss,
}: SendProgressBannerProps) => {
  const { t } = useTranslation(['signings']);
  const [loadSigning] = useApi('signings.get');

  const [phase, setPhase] = useState<SendPhase>(
    hasReviewers ? 'review' : 'preparing'
  );
  const [isSlow, setIsSlow] = useState(false);

  useEffect(() => {
    if (phase !== 'preparing') {
      return undefined;
    }

    let timeoutRef: ReturnType<typeof setTimeout> | undefined;
    let cancelled = false;

    const poll = async () => {
      const result = await loadSigning({ workflowId });
      if (cancelled) {
        return;
      }

      const state = result?.response?.data?.state;
      if (state === 'IN_REVIEW') {
        setPhase('review');
      } else if (state && state !== 'STARTING') {
        setPhase('sent');
      } else {
        timeoutRef = setTimeout(() => {
          void poll();
        }, POLL_INTERVALL_STARTING);
      }
    };

    void poll();

    return () => {
      cancelled = true;
      clearTimeout(timeoutRef);
    };
  }, [loadSigning, workflowId, phase]);

  useEffect(() => {
    if (phase !== 'preparing') {
      return undefined;
    }

    const timeoutRef = setTimeout(() => {
      setIsSlow(true);
    }, SLOW_PREPARE_THRESHOLD);

    return () => clearTimeout(timeoutRef);
  }, [phase]);

  const content = (() => {
    if (phase === 'preparing') {
      return {
        tone: 'progress' as const,
        title: t('signings:sendBanner.preparing.title', { name }),
        subtitle: isSlow
          ? t('signings:sendBanner.preparing.slowSubtitle')
          : t('signings:sendBanner.preparing.subtitle'),
      };
    }
    if (phase === 'review') {
      return {
        tone: 'success' as const,
        title: t('signings:sendBanner.review.title'),
        subtitle: t('signings:sendBanner.review.subtitle', { name }),
      };
    }
    return {
      tone: 'success' as const,
      title: t('signings:sendBanner.sent.title'),
      subtitle: t('signings:sendBanner.sent.subtitle', { name }),
    };
  })();

  return (
    <AnimatePresence mode="wait" initial={false}>
      <motion.div
        key={phase}
        initial={{ opacity: 0, y: -6 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: 6 }}
        transition={{ duration: DEFAULT_ANIMATION_DURATION }}
      >
        <SuccessBanner
          tone={content.tone}
          title={content.title}
          subtitle={content.subtitle}
          dismissLabel={t('signings:sendBanner.dismiss')}
          onDismiss={onDismiss}
        />
      </motion.div>
    </AnimatePresence>
  );
};

export default SendProgressBanner;
